import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Re-align after a slide image finishes loading only inside the product
    // media gallery. Running this on collection/product-card carousels can
    // interrupt arrow animations and make the row jump after Quick Buy closes.
    if (this.isProductMediaGallery) {
      this.addEventListener('load', this.#handleMediaLoad, true);
    }

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;

    if (slideCount <= 1) {
      this.#setupSlideshowWithoutControls();
    } else if (this.isPremiumResourceCarousel) {
      this.#setupPremiumResourceCarousel();
    } else {
      this.#setupSlideshow();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#premiumAbortController?.abort();
    this.#premiumAbortController = null;
    this.#premiumNativeAnimationAbortController?.abort();
    this.#premiumNativeAnimationAbortController = null;
    cancelAnimationFrame(this.#premiumAnimationFrame);
    clearTimeout(this.#premiumAnimationFallbackTimer);
    clearTimeout(this.#premiumSettleTimer);

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);
    this.removeEventListener('load', this.#handleMediaLoad, true);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  /**
   * Whether this slideshow belongs to the main product media gallery.
   *
   * The image-load and resize position corrections are needed for grouped
   * product media, but they should not run on collection/product-card rows.
   */
  get isProductMediaGallery() {
    return Boolean(
      this.closest(
        'media-gallery, product-media-gallery, [data-product-media-gallery], .product-media-gallery'
      )
    );
  }

  /**
   * Whether this is the outer multi-card resource/product carousel.
   *
   * Nested product-image slideshows do not carry this class, so this keeps the
   * loop-boundary fix isolated from product media and color-circle logic.
   */
  get isResourceListCarousel() {
    return this.classList.contains('resource-list__carousel');
  }

  /**
   * The premium multi-card engine is used only by the OUTER resource list.
   * Nested product-image slideshows keep Atelier's original media behavior.
   */
  get isPremiumResourceCarousel() {
    return this.isResourceListCarousel && !this.isNested;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   * @param {boolean} [options.force=false] - Re-align even when the requested slide is already current.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;

    if (this.isPremiumResourceCarousel) {
      return this.#selectPremiumResource(input, event, options);
    }

    if (!this.#scroll) return;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    const { animate = true, force = false } = options;
    if (currentSlide === requestedSlideElement && !force) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });

        // Force Safari to recalculate the timeline state on timeline refresh (after loop)
        requestAnimationFrame(() => {
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }


  /**
   * Re-aligns the slideshow to a slide after image loading, media replacement,
   * or a mobile Safari layout change. This intentionally does not emit a
   * slideshow-select event, so variant/media sync listeners are not retriggered.
   *
   * @param {number} [index=this.current] - Slide index to re-align.
   */
  refreshCurrentPosition(index = this.current) {
    if (!this.#scroll) return;
    if (this.hasAttribute('transitioning') || this.#dragging || this.#disabled) return;

    const slides = this.slides;
    if (!slides?.length) return;

    const numericIndex = Number(index);
    const safeIndex = clamp(Number.isFinite(numericIndex) ? numericIndex : 0, 0, slides.length - 1);
    const slide = slides[safeIndex];

    if (!slide) return;

    this.#scroll.to(slide, { instant: true });
    this.current = safeIndex;
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (this.isPremiumResourceCarousel) {
      const lastStart = this.#premiumLastStartIndex();
      if (previous) previous.disabled = Boolean(!this.infinite && value <= 0);
      if (next) next.disabled = Boolean(!this.infinite && value >= lastStart);
    } else {
      if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
      if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
    }
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    if (this.isPremiumResourceCarousel) {
      return this.current - this.#premiumVisibleCount();
    }

    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    if (this.isPremiumResourceCarousel) {
      return this.current + this.#premiumVisibleCount();
    }

    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    if (this.isPremiumResourceCarousel) {
      return this.current >= this.#premiumLastStartIndex();
    }

    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /** Premium resource-carousel state. */
  #premiumAbortController = null;
  #premiumAnimationFrame = 0;
  #premiumAnimationResolve = null;
  #premiumAnimationToken = 0;
  #premiumNativeAnimationAbortController = null;
  #premiumAnimationFallbackTimer = 0;
  #premiumSettleTimer = 0;
  #premiumAnimating = false;
  #premiumPointerId = null;
  #premiumPointerPending = false;
  #premiumTouchActive = false;
  #premiumNativeTouchSession = false;
  #premiumStartX = 0;
  #premiumStartY = 0;
  #premiumStartScrollLeft = 0;
  #premiumLastX = 0;
  #premiumLastMoveTime = 0;
  #premiumVelocity = 0;
  #premiumMoved = false;
  #premiumDragDelta = 0;
  #premiumPreviousSnapType = '';
  #premiumPreviousScrollBehavior = '';
  #premiumPreviousWillChange = '';
  #premiumLastWidth = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Premium outer product-carousel engine.
   *
   * Goals:
   * - one deliberate movement per arrow click
   * - page-sized arrow navigation
   * - direct, responsive pointer dragging
   * - velocity-aware snapping
   * - a clean directional crossfade at infinite boundaries
   * - no moving/cloning of live product cards
   */
  #setupPremiumResourceCarousel() {
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;

    this.#premiumAbortController?.abort();
    this.#premiumAbortController = new AbortController();
    const { signal } = this.#premiumAbortController;

    /*
     * Allow normal vertical page scrolling and pinch zooming. Horizontal
     * dragging is handled only after the gesture clearly becomes horizontal.
     */
    scroller.style.touchAction = 'pan-x pan-y pinch-zoom';
    scroller.style.cursor = 'grab';
    scroller.style.overscrollBehaviorInline = 'contain';

    scroller.addEventListener('pointerdown', this.#premiumPointerDown, { signal });
    scroller.addEventListener('pointermove', this.#premiumPointerMove, { signal });
    scroller.addEventListener('pointerup', this.#premiumPointerUp, { signal });
    scroller.addEventListener('pointercancel', this.#premiumPointerCancel, { signal });
    scroller.addEventListener('scroll', this.#premiumOnNativeScroll, { passive: true, signal });

    this.addEventListener('mouseenter', this.suspend, { signal });
    this.addEventListener('mouseleave', this.resume, { signal });
    this.addEventListener('pointerenter', this.#handlePointerEnter, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });

    this.#updateControlsVisibility();
    this.resume();

    scheduler.schedule(() => {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;

        const initialIndex = clamp(this.initialSlideIndex, 0, this.#premiumLastStartIndex());
        scroller.scrollLeft = this.#premiumTargetLeft(initialIndex);
        this.current = initialIndex;
        this.#premiumLastWidth = scroller.clientWidth;
      });

      this.#resizeObserver = new ResizeObserver(() => {
        const width = scroller.clientWidth;
        if (Math.abs(width - this.#premiumLastWidth) < 1) return;

        this.#premiumLastWidth = width;
        this.current = this.#premiumNearestIndex();
        this.#updateControlsVisibility();
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Resolve any supported select() input to a real slide index.
   *
   * @param {number|string|{id:string}} input
   * @returns {number|undefined}
   */
  #premiumResolveIndex(input) {
    const slides = this.slides;
    if (!slides?.length) return;

    if (typeof input === 'number') return input;
    if (typeof input === 'string') return Number.parseInt(input, 10);

    if ('id' in input) {
      const requestedSlide = this.refs.slides?.find(
        (slide) => slide.getAttribute('slide-id') === input.id
      );

      if (!requestedSlide) return;

      if (requestedSlide.hasAttribute('hidden')) {
        requestedSlide.setAttribute('reveal', '');
        requestedSlide.setAttribute('aria-hidden', 'false');
      }

      return this.slides?.indexOf(requestedSlide);
    }
  }

  /**
   * Premium select implementation for the outer resource carousel.
   *
   * @param {number|string|{id:string}} input
   * @param {Event} [event]
   * @param {{animate?:boolean, force?:boolean}} [options]
   */
  async #selectPremiumResource(input, event, options = {}) {
    if (this.#premiumAnimating || this.#dragging) return;

    const slides = this.slides;
    if (!slides?.length) return;

    let requestedIndex = this.#premiumResolveIndex(input);
    if (requestedIndex === undefined || Number.isNaN(requestedIndex)) return;

    event?.preventDefault();

    const { animate = true, force = false } = options;
    const lastStart = this.#premiumLastStartIndex();
    const current = clamp(this.current, 0, lastStart);

    if (requestedIndex < 0) {
      if (this.infinite && current <= 0) {
        await this.#premiumLoopBoundary('backward', event);
        return;
      }

      requestedIndex = 0;
    } else if (requestedIndex > lastStart) {
      if (this.infinite && current >= lastStart) {
        await this.#premiumLoopBoundary('forward', event);
        return;
      }

      requestedIndex = lastStart;
    }

    const targetIndex = clamp(requestedIndex, 0, lastStart);
    if (targetIndex === current && !force) return;

    const previousIndex = this.current;

    clearTimeout(this.#premiumSettleTimer);

    if (prefersReducedMotion() || !animate) {
      this.refs.scroller.scrollLeft = this.#premiumTargetLeft(targetIndex);
    } else {
      const isFinePointer =
        window.matchMedia &&
        window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      /*
       * A full desktop page contains several product cards, so it needs a
       * gentler and slightly longer movement than a phone arrow tap.
       */
      const completed = await this.#premiumAnimateToIndex(
        targetIndex,
        isFinePointer ? 460 : 360
      );

      if (!completed) return;
    }

    this.current = targetIndex;
    this.#premiumDispatchSelect(targetIndex, previousIndex, event, 'select');
  }

  /**
   * Number of complete card columns visible in the viewport.
   */
  #premiumVisibleCount() {
    const slides = this.slides;
    const { scroller } = this.refs;

    if (!slides?.length || !(scroller instanceof HTMLElement)) return 1;
    if (slides.length === 1) return 1;

    const first = slides[0];
    const second = slides[1];
    const step = Math.abs(second.offsetLeft - first.offsetLeft);

    if (step <= 0) return 1;

    return clamp(Math.floor((scroller.clientWidth + 1) / step), 1, slides.length);
  }

  #premiumLastStartIndex() {
    const slides = this.slides;
    if (!slides?.length) return 0;

    return Math.max(0, slides.length - this.#premiumVisibleCount());
  }

  /**
   * ScrollLeft needed to align a real slide with the beginning of the row.
   *
   * @param {number} index
   */
  #premiumTargetLeft(index) {
    const slides = this.slides;
    const { scroller } = this.refs;

    if (!slides?.length || !(scroller instanceof HTMLElement)) return 0;

    const safeIndex = clamp(index, 0, this.#premiumLastStartIndex());
    const base = slides[0].offsetLeft;
    const maximum = Math.max(0, scroller.scrollWidth - scroller.clientWidth);

    return clamp(slides[safeIndex].offsetLeft - base, 0, maximum);
  }

  /**
   * Find the slide start nearest the current native scroll position.
   */
  #premiumNearestIndex() {
    const slides = this.slides;
    const { scroller } = this.refs;

    if (!slides?.length || !(scroller instanceof HTMLElement)) return 0;

    const lastStart = this.#premiumLastStartIndex();
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    for (let index = 0; index <= lastStart; index += 1) {
      const distance = Math.abs(scroller.scrollLeft - this.#premiumTargetLeft(index));

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    }

    return nearestIndex;
  }

  /**
   * Stop a normal premium scroll animation without leaving the carousel locked.
   *
   * This is used when the customer starts swiping before an arrow animation has
   * fully settled. Infinite-boundary transitions are not interrupted because
   * they temporarily set #disabled.
   *
   * @param {{snap?: boolean}} [options]
   */
  #premiumInterruptAnimation(options = {}) {
    const { snap = false } = options;
    const { scroller } = this.refs;

    clearTimeout(this.#premiumSettleTimer);
    clearTimeout(this.#premiumAnimationFallbackTimer);

    this.#premiumNativeAnimationAbortController?.abort();
    this.#premiumNativeAnimationAbortController = null;

    this.#premiumAnimationToken += 1;
    cancelAnimationFrame(this.#premiumAnimationFrame);
    this.#premiumAnimationFrame = 0;

    const resolve = this.#premiumAnimationResolve;
    this.#premiumAnimationResolve = null;

    if (scroller instanceof HTMLElement) {
      /*
       * Freeze any browser-native smooth scroll at its exact current position
       * before restoring the normal carousel styles.
       */
      const frozenLeft = scroller.scrollLeft;

      try {
        scroller.scrollTo({
          left: frozenLeft,
          behavior: 'auto',
        });
      } catch (_) {
        scroller.scrollLeft = frozenLeft;
      }

      scroller.style.scrollSnapType = this.#premiumPreviousSnapType;
      scroller.style.scrollBehavior = this.#premiumPreviousScrollBehavior;
      scroller.style.willChange = this.#premiumPreviousWillChange;
    }

    this.#premiumAnimating = false;
    this.removeAttribute('transitioning');

    if (snap && scroller instanceof HTMLElement) {
      const nearestIndex = this.#premiumNearestIndex();
      scroller.scrollLeft = this.#premiumTargetLeft(nearestIndex);
      this.current = nearestIndex;
    }

    resolve?.(false);
  }

  /**
   * Animate native scrollLeft with a deliberate premium easing curve.
   *
   * The returned boolean is true only when the animation reached its target.
   * A new touch gesture can interrupt it and immediately take control.
   *
   * @param {number} targetLeft
   * @param {number} duration
   * @returns {Promise<boolean>}
   */
  /**
   * Browser-native desktop movement.
   *
   * Updating scrollLeft from JavaScript on every animation frame forces the
   * main thread to continuously repaint large product cards. Desktop now lets
   * the browser run the actual smooth scroll, while a lightweight observer
   * only watches for completion.
   *
   * @param {number} targetLeft
   * @param {number} duration Used only as a safety-timeout hint.
   * @returns {Promise<boolean>}
   */
  #premiumAnimateNativeDesktopScroll(targetLeft, duration) {
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return Promise.resolve(false);

    if (this.#premiumAnimating && !this.#disabled) {
      this.#premiumInterruptAnimation({ snap: false });
    }

    const startLeft = scroller.scrollLeft;
    const distance = targetLeft - startLeft;

    if (Math.abs(distance) < 0.5 || prefersReducedMotion()) {
      scroller.scrollLeft = targetLeft;
      return Promise.resolve(true);
    }

    const token = ++this.#premiumAnimationToken;

    this.#premiumAnimating = true;
    this.setAttribute('transitioning', '');

    this.#premiumPreviousSnapType = scroller.style.scrollSnapType;
    this.#premiumPreviousScrollBehavior = scroller.style.scrollBehavior;
    this.#premiumPreviousWillChange = scroller.style.willChange;

    /*
     * Temporarily disable snap so the browser can travel directly to the
     * requested product page without pausing at intermediate card snap points.
     */
    scroller.style.scrollSnapType = 'none';
    scroller.style.scrollBehavior = 'auto';
    scroller.style.willChange = 'scroll-position';

    this.#premiumNativeAnimationAbortController?.abort();
    const controller = new AbortController();
    this.#premiumNativeAnimationAbortController = controller;

    return new Promise((resolve) => {
      let finished = false;
      let stableFrames = 0;
      let previousLeft = scroller.scrollLeft;

      const finish = (completed) => {
        if (finished) return;
        finished = true;

        clearTimeout(this.#premiumAnimationFallbackTimer);
        cancelAnimationFrame(this.#premiumAnimationFrame);
        controller.abort();

        if (this.#premiumNativeAnimationAbortController === controller) {
          this.#premiumNativeAnimationAbortController = null;
        }

        if (completed && token === this.#premiumAnimationToken) {
          /*
           * Commit the exact target first. Restore scroll snap only after the
           * browser has painted that final position, preventing an end twitch.
           */
          scroller.scrollLeft = targetLeft;

          requestAnimationFrame(() => {
            if (token !== this.#premiumAnimationToken) {
              resolve(false);
              return;
            }

            scroller.style.scrollSnapType = this.#premiumPreviousSnapType;
            scroller.style.scrollBehavior = this.#premiumPreviousScrollBehavior;
            scroller.style.willChange = this.#premiumPreviousWillChange;
            scroller.scrollLeft = targetLeft;

            this.#premiumAnimating = false;
            this.removeAttribute('transitioning');
            this.#premiumAnimationResolve = null;
            this.#premiumAnimationFrame = 0;

            resolve(true);
          });

          return;
        }

        resolve(false);
      };

      this.#premiumAnimationResolve = finish;

      const inspectPosition = () => {
        if (
          controller.signal.aborted ||
          token !== this.#premiumAnimationToken
        ) {
          finish(false);
          return;
        }

        const currentLeft = scroller.scrollLeft;
        const remaining = Math.abs(targetLeft - currentLeft);
        const movement = Math.abs(currentLeft - previousLeft);

        if (remaining <= 0.8 && movement <= 0.2) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
        }

        previousLeft = currentLeft;

        if (stableFrames >= 3) {
          finish(true);
          return;
        }

        this.#premiumAnimationFrame =
          requestAnimationFrame(inspectPosition);
      };

      /*
       * scrollend is used when available. The position observer remains as a
       * dependable fallback for browsers with partial scrollend support.
       */
      scroller.addEventListener(
        'scrollend',
        () => {
          if (Math.abs(scroller.scrollLeft - targetLeft) <= 1.5) {
            finish(true);
          }
        },
        {
          once: true,
          signal: controller.signal,
        }
      );

      this.#premiumAnimationFallbackTimer = window.setTimeout(() => {
        if (token !== this.#premiumAnimationToken) {
          finish(false);
          return;
        }

        scroller.scrollLeft = targetLeft;
        finish(true);
      }, Math.max(900, duration + 500));

      requestAnimationFrame(() => {
        if (
          controller.signal.aborted ||
          token !== this.#premiumAnimationToken
        ) {
          finish(false);
          return;
        }

        try {
          scroller.scrollTo({
            left: targetLeft,
            behavior: 'smooth',
          });
        } catch (_) {
          scroller.scrollLeft = targetLeft;
          finish(true);
          return;
        }

        this.#premiumAnimationFrame =
          requestAnimationFrame(inspectPosition);
      });
    });
  }

  /**
   * Fallback controlled movement for non-desktop pointers.
   *
   * Mobile finger swiping itself remains browser-native. This fallback is used
   * only for programmed motion such as a mobile arrow tap.
   *
   * @param {number} targetLeft
   * @param {number} duration
   * @returns {Promise<boolean>}
   */
  #premiumAnimateFrameScroll(targetLeft, duration) {
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return Promise.resolve(false);

    if (this.#premiumAnimating && !this.#disabled) {
      this.#premiumInterruptAnimation({ snap: false });
    }

    const startLeft = scroller.scrollLeft;
    const distance = targetLeft - startLeft;

    if (Math.abs(distance) < 0.5 || prefersReducedMotion()) {
      scroller.scrollLeft = targetLeft;
      return Promise.resolve(true);
    }

    const token = ++this.#premiumAnimationToken;

    this.#premiumAnimating = true;
    this.setAttribute('transitioning', '');

    this.#premiumPreviousSnapType = scroller.style.scrollSnapType;
    this.#premiumPreviousScrollBehavior = scroller.style.scrollBehavior;
    this.#premiumPreviousWillChange = scroller.style.willChange;

    scroller.style.scrollSnapType = 'none';
    scroller.style.scrollBehavior = 'auto';
    scroller.style.willChange = 'scroll-position';

    const startTime = performance.now();

    return new Promise((resolve) => {
      let settled = false;

      const finish = (completed) => {
        if (settled) return;
        settled = true;
        resolve(completed);
      };

      this.#premiumAnimationResolve = finish;

      const frame = (time) => {
        if (token !== this.#premiumAnimationToken) {
          finish(false);
          return;
        }

        const progress = clamp((time - startTime) / duration, 0, 1);
        const eased = 1 - Math.pow(1 - progress, 3);

        scroller.scrollLeft = startLeft + distance * eased;

        if (progress < 1) {
          this.#premiumAnimationFrame = requestAnimationFrame(frame);
          return;
        }

        scroller.scrollLeft = targetLeft;

        requestAnimationFrame(() => {
          if (token !== this.#premiumAnimationToken) {
            finish(false);
            return;
          }

          scroller.style.scrollSnapType = this.#premiumPreviousSnapType;
          scroller.style.scrollBehavior = this.#premiumPreviousScrollBehavior;
          scroller.style.willChange = this.#premiumPreviousWillChange;
          scroller.scrollLeft = targetLeft;

          this.#premiumAnimating = false;
          this.removeAttribute('transitioning');
          this.#premiumAnimationResolve = null;
          this.#premiumAnimationFrame = 0;

          finish(true);
        });
      };

      this.#premiumAnimationFrame = requestAnimationFrame(frame);
    });
  }

  /**
   * Choose the safest motion engine for the active input environment.
   *
   * @param {number} targetLeft
   * @param {number} duration
   * @returns {Promise<boolean>}
   */
  #premiumAnimateScroll(targetLeft, duration) {
    const useNativeDesktopMotion =
      window.matchMedia &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    if (useNativeDesktopMotion) {
      return this.#premiumAnimateNativeDesktopScroll(
        targetLeft,
        duration
      );
    }

    return this.#premiumAnimateFrameScroll(targetLeft, duration);
  }

  /**
   * Animate to a real product-card start.
   *
   * @param {number} index
   * @param {number} duration
   */
  #premiumAnimateToIndex(index, duration) {
    return this.#premiumAnimateScroll(this.#premiumTargetLeft(index), duration);
  }

  /**
   * Infinite boundary transition.
   *
   * Instead of sweeping across every product or moving live product cards, the
   * row exits a few pixels in the requested direction, jumps while faded, and
   * enters from the opposite side. This produces one clean visual action and
   * is stable with Quick Buy, product images, and color swatches.
   *
   * @param {'forward'|'backward'} direction
   * @param {Event} [event]
   */
  async #premiumLoopBoundary(direction, event) {
    if (this.#premiumAnimating) return;

    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;

    this.#premiumAnimating = true;
    this.#disabled = true;
    this.setAttribute('transitioning', '');

    const previousIndex = this.current;
    const targetIndex = direction === 'forward' ? 0 : this.#premiumLastStartIndex();
    const exitX = direction === 'forward' ? -22 : 22;
    const enterX = -exitX;

    try {
      if (prefersReducedMotion()) {
        scroller.scrollLeft = this.#premiumTargetLeft(targetIndex);
      } else {
        const exitAnimation = scroller.animate(
          [
            { opacity: 1, transform: 'translate3d(0,0,0)' },
            { opacity: 0.18, transform: `translate3d(${exitX}px,0,0)` },
          ],
          {
            duration: 135,
            easing: 'cubic-bezier(.55,0,1,.45)',
            fill: 'forwards',
          }
        );

        await exitAnimation.finished.catch(() => {});

        scroller.scrollLeft = this.#premiumTargetLeft(targetIndex);

        const enterAnimation = scroller.animate(
          [
            { opacity: 0.18, transform: `translate3d(${enterX}px,0,0)` },
            { opacity: 1, transform: 'translate3d(0,0,0)' },
          ],
          {
            duration: 245,
            easing: 'cubic-bezier(.16,1,.3,1)',
            fill: 'forwards',
          }
        );

        await enterAnimation.finished.catch(() => {});
      }

      this.current = targetIndex;
      this.#premiumDispatchSelect(targetIndex, previousIndex, event, 'select');
    } finally {
      scroller.getAnimations().forEach((animation) => animation.cancel());
      scroller.style.opacity = '';
      scroller.style.transform = '';

      this.#premiumAnimating = false;
      this.#disabled = false;
      this.removeAttribute('transitioning');
    }
  }

  /**
   * Dispatch a normal slideshow selection event using a real slide.
   *
   * @param {number} index
   * @param {number} previousIndex
   * @param {Event|undefined} event
   * @param {'select'|'drag'|'scroll'} trigger
   */
  #premiumDispatchSelect(index, previousIndex, event, trigger) {
    const slide = this.slides?.[index];
    if (!slide || index === previousIndex) return;

    slide.setAttribute('aria-hidden', 'false');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger,
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  #premiumPointerDown = (event) => {
    const { scroller } = this.refs;

    if (!(scroller instanceof HTMLElement)) return;
    if (!event.isPrimary || event.button !== 0) return;
    if (this.disabled || this.#dragging) return;
    if (this.#disabled) return;
    if (!(event.target instanceof Element)) return;

    /*
     * MOBILE:
     * Use the browser's native touch scrolling and CSS scroll snap. The former
     * custom touch-drag engine was fighting iOS momentum after an arrow click,
     * which produced the acceleration, correction, and inconsistent swipes
     * visible in the screen recording.
     */
    if (event.pointerType === 'touch') {
      if (this.#premiumAnimating) {
        this.#premiumInterruptAnimation({ snap: true });
      }

      clearTimeout(this.#premiumSettleTimer);

      this.#premiumTouchActive = true;
      this.#premiumNativeTouchSession = true;
      this.#premiumPointerPending = false;
      this.#premiumPointerId = null;
      this.#premiumMoved = false;
      this.#premiumDragDelta = 0;

      this.pause();
      return;
    }

    /*
     * DESKTOP MOUSE / PEN:
     * Keep the controlled drag behavior.
     */
    if (this.#premiumAnimating) {
      this.#premiumInterruptAnimation({ snap: true });
    }

    clearTimeout(this.#premiumSettleTimer);

    if (
      event.target.closest(
        'button, input, select, textarea, summary, [contenteditable="true"], model-viewer'
      )
    ) {
      return;
    }

    /*
     * IMPORTANT:
     * Do not capture the pointer yet. A normal tap on a product image must
     * remain targeted at its <a> element so the product link can open.
     */
    this.#premiumPointerPending = true;
    this.#premiumPointerId = event.pointerId;
    this.#premiumStartX = event.clientX;
    this.#premiumStartY = event.clientY;
    this.#premiumStartScrollLeft = scroller.scrollLeft;
    this.#premiumLastX = event.clientX;
    this.#premiumLastMoveTime = performance.now();
    this.#premiumVelocity = 0;
    this.#premiumMoved = false;
    this.#premiumDragDelta = 0;
  };

  #premiumPointerMove = (event) => {
    const { scroller } = this.refs;

    if (!(scroller instanceof HTMLElement)) return;
    if (event.pointerId !== this.#premiumPointerId) return;
    if (!this.#premiumPointerPending && !this.#dragging) return;

    const deltaX = event.clientX - this.#premiumStartX;
    const deltaY = event.clientY - this.#premiumStartY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    this.#premiumDragDelta = deltaX;

    if (!this.#dragging) {
      /*
       * Wait until intent is clear. Vertical gestures remain page scrolling;
       * only a deliberate horizontal gesture becomes a carousel drag.
       */
      if (absX < 9 && absY < 9) return;

      if (absY > absX * 1.1) {
        this.#premiumPointerPending = false;
        this.#premiumPointerId = null;
        return;
      }

      if (absX < 10) return;

      this.#premiumPointerPending = false;
      this.#dragging = true;
      this.#premiumMoved = true;

      this.pause();
      this.setAttribute('dragging', '');

      this.#premiumPreviousSnapType = scroller.style.scrollSnapType;
      this.#premiumPreviousScrollBehavior = scroller.style.scrollBehavior;
      scroller.style.scrollSnapType = 'none';
      scroller.style.scrollBehavior = 'auto';
      scroller.style.cursor = 'grabbing';

      /*
       * Capture only after drag intent is confirmed. This keeps ordinary image
       * clicks on the product link while still making real dragging reliable.
       */
      try {
        scroller.setPointerCapture(event.pointerId);
      } catch (_) {
        // The pointer may already have been canceled by the browser.
      }

      document.addEventListener('click', preventDefault, {
        once: true,
        capture: true,
      });
    }

    event.preventDefault();
    event.stopPropagation();

    const currentTime = performance.now();
    const elapsed = Math.max(1, currentTime - this.#premiumLastMoveTime);

    /*
     * Smooth velocity sampling. Blending the newest movement with the prior
     * value prevents jittery mobile flick decisions.
     */
    const instantVelocity = (this.#premiumLastX - event.clientX) / elapsed;
    this.#premiumVelocity =
      this.#premiumVelocity * 0.65 + instantVelocity * 0.35;

    this.#premiumLastX = event.clientX;
    this.#premiumLastMoveTime = currentTime;

    const maximum = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const desired = this.#premiumStartScrollLeft - deltaX;
    scroller.scrollLeft = clamp(desired, 0, maximum);
  };

  #premiumPointerUp = async (event) => {
    const { scroller } = this.refs;

    if (!(scroller instanceof HTMLElement)) return;

    if (event.pointerType === 'touch') {
      this.#premiumTouchActive = false;
      this.#premiumScheduleNativeTouchSettle();
      return;
    }

    if (event.pointerId !== this.#premiumPointerId) return;

    /*
     * A tap never became a drag. Leave the event completely alone so the
     * product-image anchor receives its normal click.
     */
    if (!this.#dragging) {
      this.#premiumPointerPending = false;
      this.#premiumPointerId = null;
      this.#premiumMoved = false;
      this.#premiumDragDelta = 0;
      return;
    }

    const moved = this.#premiumMoved;
    const deltaX = this.#premiumDragDelta;
    const velocity = this.#premiumVelocity;

    this.#dragging = false;
    this.#premiumPointerPending = false;
    this.#premiumPointerId = null;
    this.#premiumMoved = false;

    if (scroller.hasPointerCapture(event.pointerId)) {
      try {
        scroller.releasePointerCapture(event.pointerId);
      } catch (_) {
        // Safe fallback for browsers that already released it.
      }
    }

    scroller.style.cursor = 'grab';

    if (!moved) {
      scroller.style.scrollSnapType = this.#premiumPreviousSnapType;
      scroller.style.scrollBehavior = this.#premiumPreviousScrollBehavior;
      this.removeAttribute('dragging');
      this.resume();
      return;
    }

    const lastStart = this.#premiumLastStartIndex();
    const nearestIndex = this.#premiumNearestIndex();
    const atBeginning = this.#premiumStartScrollLeft <= 1;
    const maximum = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const atEnding = this.#premiumStartScrollLeft >= maximum - 1;
    const draggedBackwardPastStart = atBeginning && deltaX > 52;
    const draggedForwardPastEnd = atEnding && deltaX < -52;

    scroller.style.scrollSnapType = this.#premiumPreviousSnapType;
    scroller.style.scrollBehavior = this.#premiumPreviousScrollBehavior;

    this.removeAttribute('dragging');

    if (this.infinite && draggedBackwardPastStart) {
      await this.#premiumLoopBoundary('backward', event);
      this.resume();
      return;
    }

    if (this.infinite && draggedForwardPastEnd) {
      await this.#premiumLoopBoundary('forward', event);
      this.resume();
      return;
    }

    /*
     * Use both distance and velocity. A short accidental movement settles to
     * the nearest card; a deliberate flick advances one card/page.
     */
    const fastFlick = Math.abs(velocity) > 0.38;
    const deliberateDistance = Math.abs(deltaX) > 42;
    const direction =
      Math.sign(velocity) || Math.sign(-deltaX);

    const targetIndex = clamp(
      fastFlick || deliberateDistance
        ? nearestIndex + direction
        : nearestIndex,
      0,
      lastStart
    );

    const previousIndex = this.current;

    const completed = await this.#premiumAnimateToIndex(
      targetIndex,
      fastFlick ? 340 : 420
    );

    if (!completed) return;

    this.current = targetIndex;
    this.#premiumDispatchSelect(targetIndex, previousIndex, event, 'drag');
    this.resume();
  };

  #premiumPointerCancel = (event) => {
    const { scroller } = this.refs;

    if (!(scroller instanceof HTMLElement)) return;

    if (event.pointerType === 'touch') {
      this.#premiumTouchActive = false;
      this.#premiumScheduleNativeTouchSettle();
      return;
    }

    if (event.pointerId !== this.#premiumPointerId) return;

    const wasDragging = this.#dragging;

    this.#premiumPointerPending = false;
    this.#premiumPointerId = null;
    this.#premiumMoved = false;
    this.#premiumDragDelta = 0;
    this.#dragging = false;

    if (scroller.hasPointerCapture(event.pointerId)) {
      try {
        scroller.releasePointerCapture(event.pointerId);
      } catch (_) {
        // No-op.
      }
    }

    scroller.style.cursor = 'grab';

    if (wasDragging) {
      scroller.style.scrollSnapType = this.#premiumPreviousSnapType;
      scroller.style.scrollBehavior = this.#premiumPreviousScrollBehavior;
      this.removeAttribute('dragging');

      const nearestIndex = this.#premiumNearestIndex();
      this.#premiumAnimateToIndex(nearestIndex, 240).then((completed) => {
        if (!completed) return;
        this.current = nearestIndex;
        this.resume();
      });

      return;
    }

    this.resume();
  };

  /**
   * Let CSS scroll snap finish a mobile swipe naturally, then only synchronize
   * the component's current index. No second JavaScript animation is applied.
   */
  #premiumScheduleNativeTouchSettle = () => {
    clearTimeout(this.#premiumSettleTimer);

    this.#premiumSettleTimer = window.setTimeout(() => {
      if (this.#premiumTouchActive) {
        this.#premiumScheduleNativeTouchSettle();
        return;
      }

      const targetIndex = this.#premiumNearestIndex();
      const previousIndex = this.current;

      /*
       * Native scroll snap may still be finishing by a fraction of a pixel.
       * Align instantly only when necessary; never start another visible motion.
       */
      const targetLeft = this.#premiumTargetLeft(targetIndex);
      const distance = Math.abs(this.refs.scroller.scrollLeft - targetLeft);

      if (distance > 1.5) {
        this.refs.scroller.scrollLeft = targetLeft;
      }

      this.current = targetIndex;
      this.#premiumDispatchSelect(
        targetIndex,
        previousIndex,
        undefined,
        'scroll'
      );

      this.#premiumNativeTouchSession = false;
      this.resume();
    }, 180);
  };

  #premiumOnNativeScroll = () => {
    if (this.#dragging || this.#premiumAnimating) return;

    /*
     * Mobile touch uses native momentum + CSS snapping. Repeated scroll events
     * simply postpone synchronization until the gesture has actually settled.
     */
    if (this.#premiumNativeTouchSession) {
      this.#premiumScheduleNativeTouchSettle();
      return;
    }

    /*
     * Desktop trackpads/wheels retain the controlled nearest-card settle.
     */
    clearTimeout(this.#premiumSettleTimer);

    this.#premiumSettleTimer = window.setTimeout(async () => {
      if (this.#dragging || this.#premiumAnimating) return;

      const targetIndex = this.#premiumNearestIndex();
      const previousIndex = this.current;

      const completed = await this.#premiumAnimateToIndex(targetIndex, 340);
      if (!completed) return;

      this.current = targetIndex;
      this.#premiumDispatchSelect(
        targetIndex,
        previousIndex,
        undefined,
        'scroll'
      );
    }, 120);
  };

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }

        if (this.isProductMediaGallery) {
          requestAnimationFrame(() => this.refreshCurrentPosition());
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };


  #handleMediaLoad = (event) => {
    if (!this.isProductMediaGallery) return;

    const image = event.target;

    if (!(image instanceof HTMLImageElement)) return;

    const loadedSlide = image.closest('slideshow-slide');
    if (!loadedSlide) return;

    requestAnimationFrame(() => {
      if (!this.isConnected) return;

      const slides = this.slides;
      if (!slides?.length) return;

      const loadedIndex = slides.indexOf(loadedSlide);
      const loadedSlideIsActive = loadedSlide.getAttribute('aria-hidden') === 'false';
      const targetIndex = loadedSlideIsActive && loadedIndex >= 0 ? loadedIndex : this.current;

      this.refreshCurrentPosition(targetIndex);
    });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}